import { useState } from 'react';
import { LayoutTemplate, Trash2 } from 'lucide-react';
import type { RequestTemplatePayload, TemplateView, WorkTemplatePayload } from '@vwt/contracts';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input, NativeSelect, Textarea, toast } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';

type Kind = 'WORK' | 'REQUEST';
type PayloadOf<K extends Kind> = K extends 'WORK' ? WorkTemplatePayload : RequestTemplatePayload;

/** Template select for a creation form, with a link to manage the templates of that kind. */
export function TemplatePicker<K extends Kind>({ kind, projectId, onApply }: { kind: K; projectId?: string | undefined; onApply: (payload: PayloadOf<K>, template: TemplateView) => void }) {
  const { t } = useTranslation();
  const templates = useApiQuery('listTemplates', { query: { kind, ...(projectId ? { projectId } : {}) } });
  const [managing, setManaging] = useState(false);
  const [chosen, setChosen] = useState('');
  const list = templates.data?.data ?? [];
  if (!list.length) return null;
  return (
    <div className="flex items-center gap-2 rounded-[var(--radius)] border border-dashed border-border px-3 py-2">
      <LayoutTemplate className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <NativeSelect
        aria-label={t('templates.use')}
        className="h-8 min-w-0 flex-1"
        value={chosen}
        onChange={(e) => {
          setChosen(e.target.value);
          const template = list.find((x) => x.id === e.target.value);
          if (template) onApply(template.payload as PayloadOf<K>, template);
        }}
      >
        <option value="">{t('templates.use')}</option>
        {list.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
            {x.project ? ` · ${x.project.key}` : ''}
          </option>
        ))}
      </NativeSelect>
      <Button type="button" variant="ghost" size="sm" onClick={() => setManaging(true)}>
        {t('templates.manage')}
      </Button>
      {managing ? <TemplateManagerDialog kind={kind} onClose={() => setManaging(false)} /> : null}
    </div>
  );
}

/** Saves a payload as a template (optionally offered only in one project). */
export function SaveTemplateDialog<K extends Kind>({ kind, payload, defaultName, projectId, onClose }: { kind: K; payload: PayloadOf<K>; defaultName: string; projectId?: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useApiMutation('createTemplate', { invalidate: ['listTemplates'] });
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [scoped, setScoped] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const submit = async () => {
    try {
      await create.mutateAsync({ idempotencyKey: key, body: { kind, name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), ...(scoped && projectId ? { projectId } : {}), payload } as never });
      toast.success(t('templates.saved'));
      onClose();
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('templates.saveTitle')} description={t(kind === 'WORK' ? 'templates.saveHintWork' : 'templates.saveHintRequest')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('templates.name')} htmlFor="template-name" required>
            <Input id="template-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />
          </Field>
          <Field label={t('templates.description')} htmlFor="template-description">
            <Textarea id="template-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} />
          </Field>
          {projectId ? (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={scoped} onChange={(e) => setScoped(e.target.checked)} /> {t('templates.onlyThisProject')}
            </label>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={!name.trim()} loading={create.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Lists the templates of a kind; editable ones can be renamed or removed. */
export function TemplateManagerDialog({ kind, onClose }: { kind: Kind; onClose: () => void }) {
  const { t } = useTranslation();
  const templates = useApiQuery('listTemplates', { query: { kind } });
  const update = useApiMutation('updateTemplate', { invalidate: ['listTemplates'] });
  const remove = useApiMutation('deleteTemplate', { invalidate: ['listTemplates'] });
  const list = templates.data?.data ?? [];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t(kind === 'WORK' ? 'templates.manageWork' : 'templates.manageRequest')} closeLabel={t('common.close')}>
        {list.length ? (
          <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
            {list.map((x) => (
              <li key={x.id} className="flex items-center gap-2 rounded-[var(--radius)] border border-border px-3 py-2">
                {x.canEdit ? (
                  <Input
                    className="h-8 min-w-0 flex-1"
                    defaultValue={x.name}
                    maxLength={120}
                    aria-label={t('templates.name')}
                    onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== x.name && void update.mutateAsync({ params: { id: x.id }, body: { name: e.target.value.trim() } }).catch(toastError)}
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-sm">{x.name}</span>
                )}
                <span className="shrink-0 text-xs text-fg-subtle">{x.project?.key ?? t('templates.everywhere')}</span>
                {x.canEdit ? (
                  <Button variant="ghost" size="iconSm" aria-label={t('templates.remove', { name: x.name })} title={t('templates.remove', { name: x.name })} onClick={() => void remove.mutateAsync({ params: { id: x.id } }).catch(toastError)}>
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-subtle">{t('templates.empty')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
